import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../services/HttpManager";
import { CambioVenta, EquipoTablero, VentaTablero } from "../../../services/v2/prospects.service";
import AdvisorsPicker from "../../../components/enrollment/advisorsPicker";
import { AdvisorSelection } from "../../../models/EnrollmentHistoryDTO";
import { Multi, rangoDe } from "../managment/enrollmentRequests/filtrosMatriculas";
import "../managment/enrollmentRequests/filtrosMatriculas.css";
import { errorTexto } from "./salesHelpers";
import { logoDe } from "./tableroView";
import "./tableroLista.css";

interface Props {
	campusId: number; ventas: VentaTablero[]; bitacora: CambioVenta[]; equipos: EquipoTablero[]; puedeEditar: boolean; mesCerrado: boolean;
	onCambio: () => void; deInforme?: boolean;
	/** Asesora: solo lo que vendió ella (Esteban, 2026-11-04), sin corregir. */
	soloMias?: boolean;
}

const PLANTEL: { [k: number]: string } = { 1: "MTY", 2: "TRC", 3: "TGU", 4: "SPS", 5: "Online", 7: "SAL" };
const POR_PAGINA = 100;
const ORIGEN: { [k: string]: string } = { tablero: "Tablero", matriculas: "Matrículas", historico: "Histórico", cruce: "Cruce con informe" };
const ACCION: { [k: string]: string } = { asesora: "Cambió quién vendió", no_es_venta: "Marcó «no es venta»", si_es_venta: "Marcó «sí es venta»", carga: "Cargó" };
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
type Preset = "" | "hoy" | "ayer" | "semana" | "mes" | "rango";
type Cuenta = "1" | "media" | "no";

const fechaCorta = (f: string, soloDia?: boolean) => {
	const [d, h] = f.split(" ");
	const [, m, dd] = d.split("-");
	if (soloDia) return `${Number(dd)} ${MESES[Number(m) - 1]}`;
	const [hh, mm] = (h || "00:00").split(":").map(Number);
	return `${Number(dd)} ${MESES[Number(m) - 1]} · ${((hh + 11) % 12) + 1}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
};
const corta = (s: string) => { const [y, m, d] = s.split("-").map(Number); return y ? `${d} ${MESES[m - 1]}` : ""; };
const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });
const dinero = (v: number, s: string) => s + " " + Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const moneda = (pais?: string, plantel?: number) => (pais === "mx" || (!pais && (plantel === 1 || plantel === 2)) ? "$" : "L");
const cuentaDe = (v: VentaTablero): Cuenta => (v.noEsVenta ? "no" : v.valor === 0.5 ? "media" : "1");

/** Barra de fechas igual a la de Matrículas: Hoy · Ayer · Esta semana · Este mes · Rango. */
const Fechas: React.FC<{ preset: Preset; from: string; to: string; onChange: (p: Preset, from: string, to: string) => void }> = ({ preset, from, to, onChange }) => {
	const [desde, setDesde] = useState(from);
	const [hasta, setHasta] = useState(to);
	const [abierto, setAbierto] = useState(false);
	const elegir = (p: Preset) => {
		if (preset === p) { onChange("", "", ""); return; }
		const r = rangoDe(p as any);
		onChange(p, r.dateFrom, r.dateTo);
	};
	const aplicar = () => {
		const [a, b] = desde && hasta && desde > hasta ? [hasta, desde] : [desde, hasta || desde];
		onChange(a ? "rango" : "", a, b);
		setAbierto(false);
	};
	const texto = preset === "rango" && from ? (from === to ? corta(from) : `${corta(from)} – ${corta(to)}`) : "";
	return (
		<div className="waFmSegs">
			{([["hoy", "Hoy"], ["ayer", "Ayer"], ["semana", "Esta semana"], ["mes", "Este mes"]] as [Preset, string][]).map(([k, l]) => (
				<button key={k} type="button" className={"waFmSeg" + (preset === k ? " activo" : "")} onClick={() => elegir(k)}>{l}</button>
			))}
			<Dropdown show={abierto} onToggle={(s) => { setAbierto(s); if (s) { setDesde(from); setHasta(to); } }} autoClose="outside">
				<Dropdown.Toggle as="button" type="button" className={"waFmSeg waFmSegRango" + (preset === "rango" ? " activo" : "")}>
					<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{texto || "Rango"}
				</Dropdown.Toggle>
				<Dropdown.Menu className="waFmMenu waFmRangoMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
					<div className="waFmRangoCampos">
						<label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} /></label>
						<label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} /></label>
					</div>
					<div className="waFmRangoPie">
						<button type="button" className="btn btn-link btn-sm" onClick={() => { setDesde(""); setHasta(""); }}>Borrar</button>
						<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!desde} onClick={aplicar}>Aplicar</button>
					</div>
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);
};

const Equipo: React.FC<{ nombre: string; equipos: EquipoTablero[]; pais?: string }> = ({ nombre, equipos, pais }) => {
	const e = equipos.find((x) => x.equipo === nombre && (!pais || x.pais === pais)) || equipos.find((x) => x.equipo === nombre);
	return <span className="tl-equipo">{e ? <img src={logoDe(e)} alt="" /> : null}{nombre}</span>;
};

/**
 * Ventas del mes (Esteban, 2026-10-30 y 2026-11-04): una por renglón, con filtros como los de Matrículas (fecha, equipo, asesora,
 * plantel, cuenta, búsqueda) y las cifras de lo filtrado arriba, para que se vea de dónde salen los números. Cada venta corregida
 * enseña su historial. Dirección corrige (su gente, o todo su país); la asesora ve solo lo suyo.
 */
const TableroVentas: React.FC<Props> = ({ campusId, ventas, bitacora, equipos, puedeEditar, mesCerrado, onCambio, deInforme, soloMias }) => {
	const http = HttpManager.getInstance();
	const [preset, setPreset] = useState<Preset>("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [fEquipos, setFEquipos] = useState<string[]>([]);
	const [fAsesoras, setFAsesoras] = useState<string[]>([]);
	const [fPlanteles, setFPlanteles] = useState<number[]>([]);
	const [fCuenta, setFCuenta] = useState<Cuenta[]>([]);
	const [q, setQ] = useState("");
	const [hasta, setHasta] = useState(POR_PAGINA);
	const [abierta, setAbierta] = useState<string | null>(null);
	const [editando, setEditando] = useState<string | null>(null);
	const [asesoras, setAsesoras] = useState<AdvisorSelection[] | null>(null);
	const [motivo, setMotivo] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const opEquipos = useMemo(() => Array.from(new Set(ventas.map((v) => v.grupo))).sort().map((g) => ({ id: g, name: g })), [ventas]);
	const opAsesoras = useMemo(() => {
		const m: { [k: string]: string } = {};
		ventas.forEach((v) => v.asesoras.forEach((a) => { if (a.name) m[a.name] = v.grupo; }));
		return Object.keys(m).sort().map((n) => ({ id: n, name: n, detalle: m[n] }));
	}, [ventas]);
	const opPlanteles = useMemo(() => Array.from(new Set(ventas.map((v) => v.plantel))).sort().map((p) => ({ id: p, name: PLANTEL[p] || String(p) })), [ventas]);
	const historial = useMemo(() => {
		const m: { [k: string]: CambioVenta[] } = {};
		bitacora.forEach((c) => { (m[c.venta] = m[c.venta] || []).push(c); });
		return m;
	}, [bitacora]);

	const filtradas = useMemo(() => {
		const t = q.trim().toLowerCase();
		return ventas.filter((v) => {
			const dia = v.fecha.slice(0, 10);
			if (from && dia < from) return false;
			if (to && dia > to) return false;
			if (fEquipos.length && !fEquipos.includes(v.grupo)) return false;
			if (fAsesoras.length && !v.asesoras.some((a) => fAsesoras.includes(a.name))) return false;
			if (fPlanteles.length && !fPlanteles.includes(v.plantel)) return false;
			if (fCuenta.length && !fCuenta.includes(cuentaDe(v))) return false;
			if (t && !(v.cliente + " " + v.curso + " " + v.asesoras.map((a) => a.name).join(" ") + " " + v.grupo).toLowerCase().includes(t)) return false;
			return true;
		});
	}, [ventas, from, to, fEquipos, fAsesoras, fPlanteles, fCuenta, q]);
	const hayFiltros = !!(from || fEquipos.length || fAsesoras.length || fPlanteles.length || fCuenta.length || q);
	const limpiar = () => { setPreset(""); setFrom(""); setTo(""); setFEquipos([]); setFAsesoras([]); setFPlanteles([]); setFCuenta([]); setQ(""); setHasta(POR_PAGINA); };

	// Cifras de lo filtrado: cuentan igual que el Resumen (las «no es venta» no suman; el monto por moneda).
	const cifras = useMemo(() => {
		let mat = 0, reportado = 0; const montos: { [s: string]: number } = {}; let corregidas = 0;
		filtradas.forEach((v) => {
			if (historial[v.tipo + "|" + v.id]) corregidas++;
			if (v.noEsVenta) return;
			mat += v.valor;
			const s = moneda(v.pais, v.plantel);
			montos[s] = (montos[s] || 0) + (v.monto || 0) * v.valor;
			if (v.reportado) reportado += (v.monto || 0) * v.valor;
		});
		return { mat, montos, reportado, corregidas };
	}, [filtradas, historial]);

	const abrirEditor = (v: VentaTablero) => { setEditando(v.clave); setAsesoras(v.asesoras.length ? v.asesoras : null); setMotivo(""); setError(null); };
	const guardar = (v: VentaTablero, accion: "asesora" | "no_es_venta" | "si_es_venta") => {
		setBusy(true); setError(null);
		http.prospectsService.ventaAjuste(campusId, { tipo: v.tipo, id: v.id, courseId: v.courseId, accion, advisors: accion === "asesora" ? (asesoras || []) : undefined, motivo })
			.then(() => { setEditando(null); onCambio(); })
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};

	return (
		<div className="tl">
			<div className="tl-cab">
				<h4>{soloMias ? "Mis Ventas del Mes" : deInforme ? "Matrículas con Asesora" : "Ventas del Mes"}</h4>
				<span className="sub">{filtradas.length} de {ventas.length}{mesCerrado ? " · mes cerrado" : ""}{deInforme ? " · las matrículas de WAPP a las que se les encontró asesora" : ""}</span>
				<div className="tl-busca">
					<FontAwesomeIcon icon={solid("magnifying-glass")} />
					<input type="search" value={q} placeholder="Cliente, curso, asesora o equipo" aria-label="Buscar" onChange={(e) => { setQ(e.target.value); setHasta(POR_PAGINA); }} />
				</div>
			</div>
			<div className="waFmBarra">
				<div className="waFmBarraFecha">
					<span className="waFmBarraEtiqueta">Fecha de venta</span>
					<Fechas preset={preset} from={from} to={to} onChange={(p, a, b) => { setPreset(p); setFrom(a); setTo(b); setHasta(POR_PAGINA); }} />
				</div>
				<div className="waFmBarraListas">
					{soloMias ? null : <Multi titulo="Equipo" opciones={opEquipos} elegidos={fEquipos} onChange={(v) => { setFEquipos(v); setHasta(POR_PAGINA); }} />}
					{soloMias ? null : <Multi titulo="Asesora" buscar opciones={opAsesoras} elegidos={fAsesoras} onChange={(v) => { setFAsesoras(v); setHasta(POR_PAGINA); }} />}
					{opPlanteles.length > 1 ? <Multi titulo="Plantel" opciones={opPlanteles} elegidos={fPlanteles} onChange={(v) => { setFPlanteles(v); setHasta(POR_PAGINA); }} /> : null}
					<Multi titulo="Cuenta" opciones={[{ id: "1", name: "Cuenta 1" }, { id: "media", name: "Cuenta ½" }, { id: "no", name: "No es Venta" }]} elegidos={fCuenta} onChange={(v) => { setFCuenta(v as Cuenta[]); setHasta(POR_PAGINA); }} />
					{hayFiltros ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={limpiar}>Limpiar Filtros</button> : null}
				</div>
			</div>
			<div className="tl-kpis">
				<div className="tl-kpi"><small>Ventas</small><b>{filtradas.length}</b></div>
				<div className="tl-kpi"><small>Matrículas</small><b>{num(cifras.mat)}</b></div>
				{Object.entries(cifras.montos).map(([s, v]) => <div className="tl-kpi" key={s}><small>Monto{Object.keys(cifras.montos).length > 1 ? " " + s : ""}</small><b>{dinero(v, s)}</b></div>)}
				{cifras.reportado > 0 ? <div className="tl-kpi"><small>Reportado por asesora</small><b className="oro">{dinero(cifras.reportado, Object.keys(cifras.montos)[0] || "L")}</b></div> : null}
				{cifras.corregidas > 0 ? <div className="tl-kpi"><small>Con cambios</small><b>{cifras.corregidas}</b></div> : null}
			</div>
			<div className="tl-tabla-marco waScrollX">
				<table className="tl-tabla">
					<thead><tr>
						<th>Fecha</th><th>Cliente</th><th>Asesora</th>{opPlanteles.length > 1 ? <th>Plantel</th> : null}<th className="tl-num">Monto</th><th>Cuenta</th>{puedeEditar ? <th /> : null}
					</tr></thead>
					<tbody>
						{filtradas.length === 0 ? <tr><td colSpan={7} className="tl-vacio">{hayFiltros ? "Sin ventas con esos filtros." : "Sin ventas este mes."}</td></tr> : null}
						{filtradas.slice(0, hasta).map((v) => {
							const claveVenta = v.tipo + "|" + v.id;
							const cambios = historial[claveVenta] || [];
							const abiertaEsta = abierta === v.clave;
							const editandoEsta = editando === v.clave;
							const cuenta = cuentaDe(v);
							return (
								<React.Fragment key={v.clave}>
									<tr className={(v.noEsVenta ? "tl-tachada" : "") + (abiertaEsta || editandoEsta ? " tl-abierta" : "")}>
										<td className="tl-fecha" title={v.soloDia ? "Día del depósito" : undefined}>{fechaCorta(v.fecha, v.soloDia)}</td>
										<td><span className="tl-nom">{v.cliente}</span><span className="tl-sub">{v.curso}</span></td>
										<td>
											{v.asesoras.length ? <span className="tl-nom">{v.asesoras.map((a) => a.name).join(" + ")}</span> : <span className="tl-chip oro">Sin asesora</span>}
											<span className="tl-sub"><Equipo nombre={v.grupo} equipos={equipos} pais={v.pais} /></span>
										</td>
										{opPlanteles.length > 1 ? <td>{PLANTEL[v.plantel] || v.plantel}</td> : null}
										<td className="tl-num">{v.monto ? dinero(v.monto, moneda(v.pais, v.plantel)) : <span className="tl-sub">—</span>}{v.reportado ? <span className="tl-sub">reportado</span> : null}</td>
										<td>
											<span className={"tl-chip " + (cuenta === "no" ? "rojo" : cuenta === "media" ? "oro" : "verde")}>{cuenta === "no" ? "No es venta" : cuenta === "media" ? "½" : "1"}</span>
											{cambios.length ? <> <button type="button" className="tl-chip boton" onClick={() => setAbierta(abiertaEsta ? null : v.clave)} aria-expanded={abiertaEsta}>
												{cambios.length === 1 ? "1 cambio" : cambios.length + " cambios"}</button></> : null}
										</td>
										{puedeEditar ? <td className="tl-num">{v.editable && !editandoEsta ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" onClick={() => abrirEditor(v)}>Corregir</button> : null}</td> : null}
									</tr>
									{abiertaEsta || editandoEsta ? (
										<tr className="tl-detalle"><td colSpan={7}>
											<div className="tl-detalle-caja">
												{cambios.length ? (
													<div className="tl-historial">
														{cambios.map((c) => (
															<div key={c.id}>
																<span className="tl-cuando">{fechaCorta(c.cuando)}</span>
																<b>{c.quien}</b><span>{ACCION[c.accion] || c.accion}</span>
																{c.accion === "asesora" ? <span className="tl-sub" style={{ display: "inline" }}>{c.antes || "Sin asesora"} → {c.despues}</span> : null}
																{c.motivo ? <span className="tl-sub" style={{ display: "inline" }}>«{c.motivo}»</span> : null}
																<span className="tl-sub" style={{ display: "inline" }}>desde {ORIGEN[c.origen] || c.origen}</span>
															</div>
														))}
													</div>
												) : null}
												{editandoEsta ? (
													<div className="tl-editar">
														<AdvisorsPicker campusId={v.plantel} value={asesoras} onChange={setAsesoras} disabled={busy} allowNone={false} />
														{v.tipo === "pre" ? <span className="tl-sub">El cambio aplica a toda la solicitud (todas sus personas).</span> : null}
														<input type="text" value={motivo} maxLength={300} placeholder="Motivo (obligatorio para «No es venta»)" onChange={(e) => setMotivo(e.target.value)} disabled={busy} />
														{error ? <div className="aviso error">{error}</div> : null}
														<div className="tl-botones">
															<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !asesoras || !asesoras.length} onClick={() => guardar(v, "asesora")}>Guardar asesora</button>
															{v.noEsVenta
																? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => guardar(v, "si_es_venta")}>Sí es venta</button>
																: <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy || !motivo.trim()} onClick={() => guardar(v, "no_es_venta")}>No es venta</button>}
															<button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setEditando(null)}>Cancelar</button>
														</div>
													</div>
												) : null}
											</div>
										</td></tr>
									) : null}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
			{filtradas.length > hasta ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill tl-mas" onClick={() => setHasta(hasta + POR_PAGINA)}>Ver {Math.min(POR_PAGINA, filtradas.length - hasta)} más</button> : null}
		</div>
	);
};

/** Historial de cambios del mes: todo cambio manual que movió las ventas, con quién, cuándo, qué había y qué quedó. */
export const BitacoraVentas: React.FC<{ ventas: VentaTablero[]; bitacora: CambioVenta[] }> = ({ ventas, bitacora }) => {
	const [q, setQ] = useState("");
	const [fQuien, setFQuien] = useState<string[]>([]);
	const [fAccion, setFAccion] = useState<string[]>([]);
	const cliente = useMemo(() => {
		const m: { [k: string]: string } = {};
		ventas.forEach((v) => { const k = v.tipo + "|" + v.id; m[k] = m[k] ? m[k] + " + " + v.curso : v.cliente + " · " + v.curso; });
		return m;
	}, [ventas]);
	const opQuien = useMemo(() => Array.from(new Set(bitacora.map((c) => c.quien))).sort().map((n) => ({ id: n, name: n })), [bitacora]);
	const opAccion = useMemo(() => Array.from(new Set(bitacora.map((c) => c.accion))).map((a) => ({ id: a, name: ACCION[a] || a })), [bitacora]);
	const filtrados = useMemo(() => {
		const t = q.trim().toLowerCase();
		return bitacora.filter((c) => (!fQuien.length || fQuien.includes(c.quien)) && (!fAccion.length || fAccion.includes(c.accion))
			&& (!t || ((cliente[c.venta] || "") + " " + c.quien + " " + (c.antes || "") + " " + (c.despues || "") + " " + (c.motivo || "")).toLowerCase().includes(t)));
	}, [bitacora, fQuien, fAccion, q, cliente]);
	return (
		<div className="tl">
			<div className="tl-cab">
				<h4>Historial de Cambios</h4>
				<span className="sub">{bitacora.length ? `${filtrados.length} de ${bitacora.length}` : "Nadie ha cambiado a mano las ventas de este mes"}</span>
				{bitacora.length ? (
					<div className="tl-busca">
						<FontAwesomeIcon icon={solid("magnifying-glass")} />
						<input type="search" value={q} placeholder="Cliente, quién, motivo" aria-label="Buscar" onChange={(e) => setQ(e.target.value)} />
					</div>
				) : null}
			</div>
			{bitacora.length ? (
				<>
					<div className="waFmBarra">
						<div className="waFmBarraListas">
							<Multi titulo="Quién" opciones={opQuien} elegidos={fQuien} onChange={setFQuien} />
							<Multi titulo="Qué Hizo" opciones={opAccion} elegidos={fAccion} onChange={setFAccion} />
							{fQuien.length || fAccion.length || q ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => { setFQuien([]); setFAccion([]); setQ(""); }}>Limpiar Filtros</button> : null}
						</div>
					</div>
					<div className="tl-tabla-marco waScrollX">
						<table className="tl-tabla">
							<thead><tr><th>Cuándo</th><th>Quién</th><th>Qué hizo</th><th>Venta</th><th>Antes</th><th>Después</th><th>Motivo</th></tr></thead>
							<tbody>
								{filtrados.length === 0 ? <tr><td colSpan={7} className="tl-vacio">Sin cambios con esos filtros.</td></tr> : null}
								{filtrados.map((c) => (
									<tr key={c.id}>
										<td className="tl-fecha">{fechaCorta(c.cuando)}</td>
										<td className="tl-nom">{c.quien}</td>
										<td>{ACCION[c.accion] || c.accion}<span className="tl-sub">desde {ORIGEN[c.origen] || c.origen}</span></td>
										<td>{cliente[c.venta] || (c.venta.startsWith("hist|") ? "Informe del mes" : "Venta de otro mes o plantel")}</td>
										<td className="tl-sub" style={{ display: "table-cell" }}>{c.antes || (c.accion === "asesora" ? "Sin asesora" : "")}</td>
										<td>{c.despues || ""}</td>
										<td className="tl-sub" style={{ display: "table-cell" }}>{c.motivo || ""}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			) : null}
		</div>
	);
};

export default TableroVentas;
